import { useMemo } from "react";
import { Icon, Typography, Button } from "@mui/material";
import moment from "moment";

import { subscriptionHelper } from "@/features/subscriptions/helpers/subscriptionHelper";
import RpImageTextInline from "@/components/RpImageTextInline";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import RpSvgIcon from "@/components/icons/RpSvgIcon";

type PreviousSelectedPlanProps = {
  data: Record<string, any>;
  isChangingUpcomingPackage?: boolean;
};

const RpSvgIconComponent = RpSvgIcon as any;

const getPreviousPackageInfo = (
  locationInfo: Record<string, any> = {},
  isNewSubscription: boolean
) => {
  const previousPackageId = isNewSubscription
    ? locationInfo?.fk_package_id
    : locationInfo?.upcoming_package_id;

  return locationInfo?.packageData?.find(
    (packageInfo: Record<string, any>) =>
      String(packageInfo?.id) === String(previousPackageId)
  );
};

export default function PreviousSelectedPlan({
  data,
  isChangingUpcomingPackage,
}: PreviousSelectedPlanProps) {
  const isNewSubscription = useMemo(() => {
    return subscriptionHelper.checkIsNewSubscription(data?.locationInfo);
  }, [data.locationInfo]);

  const planDetails = useMemo(() => {
    const { startDate, endDate } = subscriptionHelper.calculateSubscriptionDate(
      data?.packageInfo,
      data?.locationInfo,
      isNewSubscription ? false : isChangingUpcomingPackage
    );
    let details: Record<string, any> = {
      plan: data.locationInfo.upcoming_package,
      startDate: startDate.format("MMM D, YYYY"),
      endDate: endDate.format("MMM D, YYYY"),
      price: data?.locationInfo?.upcoming_package_amount,
      sub_fee: data?.locationInfo?.upcoming_subscription_fee,
      total:
        data?.locationInfo?.upcoming_package_amount +
        data?.locationInfo?.upcoming_subscription_fee,
      duration: data?.locationInfo?.upcoming_package_duration,
    };

    if (isNewSubscription) {
      const startDate = moment(data?.locationInfo?.subscription_start_date);
      const endDate = moment(startDate)
        .add(parseInt(data?.locationInfo?.duration), "month")
        .subtract(1, "day");

      details = {
        ...details,
        startDate: startDate.isValid() ? startDate.format("MMM D, YYYY") : "",
        endDate: endDate.isValid() ? endDate.format("MMM D, YYYY") : "",
        plan: data.locationInfo.subscription_package,
        price: data?.locationInfo?.media_fee,
        sub_fee: data?.locationInfo?.extra_charge,
        total: data?.locationInfo?.media_fee + data?.locationInfo?.extra_charge,
        CurrentPaymentType: data?.locationInfo?.current_package_payment_type,
        duration: data?.locationInfo?.duration,
      };
    }

    const previousPackageInfo = getPreviousPackageInfo(
      data?.locationInfo,
      isNewSubscription
    );
    const currency = String(previousPackageInfo?.currency || "")
      .trim()
      .toUpperCase();
    const isCadCurrency = currency === "CAD";
    const showCadConversion =
      currency.includes("USD") && currency.includes("CAD");
    const price = isCadCurrency
      ? (previousPackageInfo?.cad_amount ?? details.price)
      : details.price;
    const subFee = isCadCurrency
      ? (previousPackageInfo?.cad_extra_charge ?? details.sub_fee)
      : details.sub_fee;

    details = {
      ...details,
      price,
      sub_fee: subFee,
      total: Number(price) + Number(subFee),
      cadPrice: previousPackageInfo?.cad_amount,
      cadSubFee: previousPackageInfo?.cad_extra_charge,
      cadTotal:
        Number(previousPackageInfo?.cad_amount) +
        Number(previousPackageInfo?.cad_extra_charge),
      displayCurrency: isCadCurrency ? "CAD" : "USD",
      showCadConversion,
    };

    return details;
  }, [data, isNewSubscription, isChangingUpcomingPackage]);

  const currencySuffix = " per month";
  const renderAmount = (value: any, cadValue: any) => {
    const isCadCurrency = planDetails.displayCurrency === "CAD";

    if (planDetails.showCadConversion) {
      return (
        <span className="currency">
          <CurrencyFormateText value={value} renderText={(price) => price} /> (
          <CurrencyFormateText
            value={cadValue}
            prefix="CAD $"
            renderText={(price) => price}
          />
          ){currencySuffix}
        </span>
      );
    }

    return (
      <CurrencyFormateText
        value={value}
        prefix={isCadCurrency ? "CAD $" : "$"}
        suffix={isCadCurrency ? currencySuffix : ` USD${currencySuffix}`}
      />
    );
  };

  return (
    <div className="card mb-2 previous-location-data">
      <div className="card-body">
        <div className="row">
          <div className="col-lg-3 col-xl-4 mb-2 ">
            <Typography
              className="mr-2 location-name"
              color="text.primary"
              component="span"
              variant="body2"
            >
              {`${data.locationInfo.location_name} (${data.locationInfo.participantName})`}
            </Typography>
            <Typography className="tag" variant="body2">
              (OLD PLAN)
            </Typography>
          </div>
          <div className="col-lg-3 col-md-6 mb-2">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="bucket"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title">Media plan</div>
                  <div className="val">
                    {`${planDetails.plan} - ${planDetails.duration} Months `}
                  </div>
                </>
              }
            />
          </div>
          <div className="col-lg-3 col-md-6 mb-2">
            <RpImageTextInline
              alignCenter={false}
              svgIcon="campaign_calender"
              imageProps={{ color: "primary" }}
              text={
                <>
                  <div className="title">Plan dates</div>
                  <div className="val">{`${planDetails.startDate} - ${planDetails.endDate}`}</div>
                </>
              }
            />
          </div>

          <div className="col-xl-2 col-lg-3 col-md-12 mb-2 text-lg-right d-none d-lg-block">
            <div className="title">
              <Icon
                component={RpSvgIconComponent}
                name="money"
                color="primary"
                fontSize="small"
                className="mr-1"
              />
              Price
            </div>
            <div className="val">
              {renderAmount(planDetails.price, planDetails.cadPrice)}
            </div>
          </div>
        </div>

        <div className="row justify-content-end my-2 d-lg-none">
          <div className="col-6 col-lg-3 val">Price</div>
          <div className="col-6 col-lg-3 col-xl-2 text-right val">
            {renderAmount(planDetails.price, planDetails.cadPrice)}
          </div>
        </div>

        <div className="row my-2">
          <div className="col-lg-6 col-xl-7">
            {data?.locationInfo?.upcoming_is_brand_match === 1 ? (
              <Button
                variant="contained"
                onClick={() => {}}
                className="px-4 brand-match-btn"
                disabled
              >
                Brand Match &nbsp;{" "}
                <CurrencyFormateText
                  value={data?.locationInfo?.upcoming_brand_match_amount}
                  suffix=" USD"
                />
              </Button>
            ) : null}
          </div>
          <div className="col-lg-6 col-xl-5 d-flex justify-content-between">
            <div className="subs">Fee</div>
            <div className="subs">
              {renderAmount(planDetails.sub_fee, planDetails.cadSubFee)}
            </div>
          </div>
        </div>
        <div className="row justify-content-end">
          <div className="col-lg-12 col-xl-12">
            <hr />
          </div>
        </div>
        <div className="row justify-content-end">
          <div className="col-3 total">Total Budget:</div>
          <div className="col-9 col-lg-3 col-xl-2 text-right total">
            {renderAmount(planDetails.total, planDetails.cadTotal)}
          </div>
        </div>
      </div>
    </div>
  );
}
